import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function useTareas(user) {
  const [tareas, setTareas] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [labores, setLabores] = useState([]);
  const [centros, setCentros] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [tareasRes, trabajadoresRes, lotesRes, materialesRes, laboresRes, centrosRes] = await Promise.all([
        axios.get(`${API}/tareas`),
        axios.get(`${API}/trabajadores`),
        axios.get(`${API}/lotes`),
        axios.get(`${API}/materiales`).catch(() => ({ data: [] })),
        axios.get(`${API}/labores`).catch(() => ({ data: [] })),
        axios.get(`${API}/centros`).catch(() => ({ data: [] })),
      ]);
      
      setTareas(tareasRes.data);
      setTrabajadores(trabajadoresRes.data);
      setLotes(lotesRes.data);
      setMateriales(materialesRes.data);
      setLabores(laboresRes.data);
      setCentros(centrosRes.data);
      
      // Inicializar labores si no existen
      if (laboresRes.data.length === 0) {
        await axios.post(`${API}/labores/inicializar`);
        const laboresRefresh = await axios.get(`${API}/labores`);
        setLabores(laboresRefresh.data);
      }
      
      // Cargar notificaciones del usuario
      if (user?.id) {
        const notifRes = await axios.get(`${API}/notificaciones/${user.id}`).catch(() => ({ data: [] }));
        setNotificaciones(notifRes.data);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createTarea = async (tareaData, userName) => {
    try {
      const dataToSend = {
        ...tareaData,
        creado_por: userName,
      };
      await axios.post(`${API}/tareas`, dataToSend);
      toast.success('Tarea creada exitosamente');
      await fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear tarea');
      return false;
    }
  };

  const updateTarea = async (tareaId, tareaData) => {
    try {
      await axios.put(`${API}/tareas/${tareaId}`, tareaData);
      toast.success('Tarea actualizada exitosamente');
      await fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar tarea');
      return false;
    }
  };

  const deleteTarea = async (tareaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta tarea?')) return false;
    
    try {
      await axios.delete(`${API}/tareas/${tareaId}`);
      toast.success('Tarea eliminada exitosamente');
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al eliminar tarea');
      return false;
    }
  };

  const updateEstadoTarea = async (tareaId, nuevoEstado, userName) => {
    try {
      await axios.put(`${API}/tareas/${tareaId}/estado`, {
        nuevo_estado: nuevoEstado,
        usuario: userName
      });
      toast.success('Estado actualizado');
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al actualizar estado');
      return false;
    }
  };

  const toggleSubtarea = async (tareaId, subtareaId, userName) => {
    try {
      await axios.put(`${API}/tareas/${tareaId}/subtarea/${subtareaId}/toggle`, null, {
        params: { usuario: userName }
      });
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al actualizar subtarea');
      return false;
    }
  };

  const addComentario = async (tareaId, texto, userName) => {
    try {
      await axios.post(`${API}/tareas/${tareaId}/comentarios`, {
        texto,
        usuario: userName
      });
      toast.success('Comentario agregado');
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al agregar comentario');
      return false;
    }
  };

  const uploadAdjunto = async (tareaId, file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es muy grande (máximo 10MB)');
      return false;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/tareas/${tareaId}/adjuntos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Archivo adjuntado exitosamente');
      await fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
      return false;
    }
  };

  const deleteAdjunto = async (tareaId, index) => {
    if (!window.confirm('¿Eliminar este archivo?')) return false;
    
    try {
      await axios.delete(`${API}/tareas/${tareaId}/adjuntos/${index}`);
      toast.success('Archivo eliminado');
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al eliminar archivo');
      return false;
    }
  };

  const aplicarMateriales = async (tareaId) => {
    if (!window.confirm('¿Descontar los materiales del inventario? Esta acción no se puede deshacer.')) {
      return false;
    }

    try {
      const response = await axios.post(`${API}/tareas/${tareaId}/aplicar-materiales`);
      if (response.data.success) {
        toast.success('Materiales descontados exitosamente');
        await fetchData();
        return true;
      } else {
        toast.error(`Errores: ${response.data.errores.join(', ')}`);
        return false;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar materiales');
      return false;
    }
  };

  const marcarNotificacionLeida = async (notifId) => {
    try {
      await axios.put(`${API}/notificaciones/${notifId}/marcar-leida`);
      setNotificaciones(notificaciones.map(n => 
        n.id === notifId ? { ...n, leida: true } : n
      ));
      return true;
    } catch (error) {
      console.error('Error al marcar notificación como leída');
      return false;
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      await axios.put(`${API}/notificaciones/${user.id}/marcar-todas-leidas`);
      setNotificaciones(notificaciones.map(n => ({ ...n, leida: true })));
      toast.success('Todas las notificaciones marcadas como leídas');
      return true;
    } catch (error) {
      toast.error('Error al marcar notificaciones');
      return false;
    }
  };

  const loadCentrosByLote = async (loteId) => {
    if (!loteId) return [];
    
    try {
      const response = await axios.get(`${API}/centros?lote_id=${loteId}`);
      return response.data;
    } catch (error) {
      console.error('Error al cargar centros:', error);
      return [];
    }
  };

  const asignarResponsable = async (tareaId, responsableId, responsableNombre) => {
    try {
      await axios.put(`${API}/tareas/${tareaId}/asignar-responsable`, {
        responsable_id: responsableId,
        responsable_nombre: responsableNombre
      });
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al asignar responsable');
      return false;
    }
  };

  const asignarRevisor = async (tareaId, revisorId, revisorNombre) => {
    try {
      await axios.put(`${API}/tareas/${tareaId}/asignar-revisor`, {
        revisor_id: revisorId,
        revisor_nombre: revisorNombre
      });
      await fetchData();
      return true;
    } catch (error) {
      toast.error('Error al asignar revisor');
      return false;
    }
  };

  return {
    // States
    tareas,
    trabajadores,
    lotes,
    materiales,
    labores,
    centros,
    notificaciones,
    loading,
    
    // Actions
    fetchData,
    createTarea,
    updateTarea,
    deleteTarea,
    updateEstadoTarea,
    toggleSubtarea,
    addComentario,
    uploadAdjunto,
    deleteAdjunto,
    aplicarMateriales,
    marcarNotificacionLeida,
    marcarTodasLeidas,
    loadCentrosByLote,
    asignarResponsable,
    asignarRevisor,
  };
}
